/* Site-wide country iconography policy: wherever a country/jurisdiction is shown
   as an icon/logo field, render its flag. Unknown countries fall back to a code
   monogram — never a broken or generic image. Flags are inline SVG (crisp at any
   size, zero network dependency). Building blocks:
   - <CountryFlag>  avatar-style flag (circle/rounded square)
   - <FlagRect>     polished mini rectangular flag chip (badges, rows)
   - <SvgFlagNode>  flag node for use INSIDE an existing <svg> (radar visuals) */
import type { ReactElement } from "react";

const NORMALIZE: Record<string, string> = {
  us: "US", usa: "US", "united states": "US", "united states of america": "US",
  uk: "UK", gb: "UK", "united kingdom": "UK", "great britain": "UK",
  eu: "EU", "european union": "EU", europe: "EU",
};

export const countryCode = (v?: string) =>
  v ? NORMALIZE[v.trim().toLowerCase()] || null : null;

/* Flag artwork in a 60x40 coordinate space (no outer <svg> — reusable everywhere) */

const UsContent = () => (
  <g>
    <rect width="60" height="40" fill="#F5F5F5" />
    {[0, 2, 4, 6, 8, 10, 12].map((i) => (
      <rect key={i} y={(i * 40) / 13} width="60" height={40 / 13} fill="#B22234" />
    ))}
    <rect width="26" height={(7 * 40) / 13} fill="#3C3B6E" />
    {Array.from({ length: 5 }).map((_, row) =>
      Array.from({ length: row % 2 === 0 ? 6 : 5 }).map((_, col) => (
        <circle key={`${row}-${col}`} r="1.05" fill="#fff"
          cx={2.6 + col * 4.2 + (row % 2 === 1 ? 2.1 : 0)} cy={2.4 + row * 4.2} />
      )))}
  </g>
);

const UkContent = () => (
  <g>
    <rect width="60" height="40" fill="#012169" />
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="8" />
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="3.4" />
    <path d="M30,0 V40 M0,20 H60" stroke="#fff" strokeWidth="13" />
    <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="7.5" />
  </g>
);

const euStar = (cx: number, cy: number) => {
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? 2.3 : 0.95;
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    pts.push(`${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(" ");
};

const EuContent = () => (
  <g>
    <rect width="60" height="40" fill="#003399" />
    {Array.from({ length: 12 }).map((_, i) => {
      const a = (i * Math.PI) / 6;
      return <polygon key={i} fill="#FFCC00"
        points={euStar(30 + 12 * Math.cos(a), 20 + 12 * Math.sin(a))} />;
    })}
  </g>
);

export const FLAG_CONTENT: Record<string, () => ReactElement> = {
  US: UsContent, UK: UkContent, EU: EuContent,
};

const svgProps = {
  className: "absolute inset-0 h-full w-full",
  preserveAspectRatio: "xMidYMid slice" as const,
};

export const CountryFlag = ({ country, size = 36, rounded = "rounded-full", className = "" }: {
  country: string; size?: number; rounded?: string; className?: string;
}) => {
  const code = countryCode(country) || (country || "??").slice(0, 2).toUpperCase();
  const Flag = FLAG_CONTENT[code];
  return (
    <span aria-hidden title={country} data-testid={`country-flag-${code}`}
      className={`relative inline-grid place-items-center overflow-hidden shrink-0 border border-white/[0.18] ${Flag ? "" : "bg-[#A78BFA]/[0.08] border-[#A78BFA]/35"} ${rounded} ${className}`}
      style={{ width: size, height: size }}>
      {Flag ? (
        <>
          <svg viewBox="0 0 60 40" {...svgProps}><Flag /></svg>
          <span aria-hidden className="absolute inset-0 bg-gradient-to-b from-white/[0.16] via-transparent to-black/[0.18] pointer-events-none" />
        </>
      ) : (
        <span className="font-mono text-[11px] font-bold text-[#A78BFA]">{code}</span>
      )}
    </span>
  );
};

/* Polished mini rectangular flag chip — pulse rows, priority rows, badges */
export const FlagRect = ({ country, className = "" }: { country?: string; className?: string }) => {
  const code = countryCode(country || "");
  const Flag = code ? FLAG_CONTENT[code] : null;
  if (!Flag) return null;
  return (
    <span aria-hidden title={country} data-testid={`country-flag-${code}`}
      className={`relative inline-block h-[15px] w-[22px] rounded-[3px] overflow-hidden shrink-0 ring-1 ring-white/[0.28] shadow-[0_1px_3px_rgba(0,0,0,0.5)] ${className}`}>
      <svg viewBox="0 0 60 40" {...svgProps}><Flag /></svg>
      <span aria-hidden className="absolute inset-0 bg-gradient-to-b from-white/[0.22] via-transparent to-black/[0.22] pointer-events-none" />
    </span>
  );
};

/* Flag node for use INSIDE an <svg> (e.g. the homepage Regulation Radar orbital).
   Renders a circle-cropped flag with a glass highlight + hairline ring. */
export const SvgFlagNode = ({ code, cx, cy, r, ring, ringColor = "#A78BFA" }: {
  code: string; cx: number; cy: number; r: number; ring?: boolean; ringColor?: string;
}) => {
  const Flag = FLAG_CONTENT[code];
  const id = `flag-clip-${code}-${Math.round(cx)}-${Math.round(cy)}`;
  if (!Flag) {
    return <circle cx={cx} cy={cy} r={r * 0.7} fill="#6b7280" opacity="0.7" />;
  }
  const s = (2 * r) / 40; // flag height covers the circle, width center-cropped
  return (
    <g>
      <defs>
        <clipPath id={id}><circle cx={cx} cy={cy} r={r} /></clipPath>
        <radialGradient id={`${id}-gloss`} cx="0.35" cy="0.25" r="0.9">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.32" />
          <stop offset="45%" stopColor="#fff" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.28" />
        </radialGradient>
      </defs>
      <circle cx={cx} cy={cy} r={r + 1.4} fill="#0a0b10" opacity="0.9" />
      <g clipPath={`url(#${id})`}>
        <g transform={`translate(${cx - 30 * s}, ${cy - 20 * s}) scale(${s})`}><Flag /></g>
        <circle cx={cx} cy={cy} r={r} fill={`url(#${id}-gloss)`} />
      </g>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="rgba(255,255,255,0.45)" strokeWidth="0.7" />
      {ring && (
        <circle className="nl-radar-ring" cx={cx} cy={cy} r={r + 2.6} fill="none"
          stroke={ringColor} strokeWidth="1" opacity="0.5" />
      )}
    </g>
  );
};
